import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { functions } from '../../../firebase';
import { toast } from 'sonner';
import {
  isDateWithinInclusiveRange,
  normalizeHistoryDateRanges,
  type InclusiveDateRange,
} from 'general/usage';

// Types
export interface ChartData {
  timestamp: string;
  level: number;
  formattedTime: string;
  rawTimestamp: Date;
  deviceId: string;
}
export interface ChartDataTemp {
  timestamp: string;
  temp: number;
  formattedTime: string;
  rawTimestamp: Date;
  deviceId: string;
}

export interface ChartDataLiter {
  timestamp: string;
  liter: number;
  formattedTime: string;
  rawTimestamp: Date;
  deviceId: string;
}

export interface Log {
  Te: number | null;
  EE: number | null;
  l: number | null;
  m2: number | null;
  cm: number | null;
  log_count: number;
  createTime: Date;
  id: string;
}

export interface ProcessedLevelData {
  levelData: ChartData[];
  tempData: ChartDataTemp[];
  literData: ChartDataLiter[];
  weekSummaryLevelData: ChartData[];
  maxLevel: number;
  maxLiter: number;
  latestLog?: ChartData;
}

// API function
const getLogs = async (
  deviceId: string,
  dateRange: Pick<DateRange, 'from' | 'to'> | undefined
): Promise<Log[]> => {
  if (!deviceId) {
    throw new Error('Device ID is required');
  }

  if (!dateRange?.from || !dateRange?.to) {
    return [];
  }

  const functionRef = functions.httpsCallable('getFlowLogs');

  const response: any = await functionRef({
    deviceId,
    start: format(dateRange.from, 'yyyy-MM-dd'),
    end: format(dateRange.to, 'yyyy-MM-dd'),
  });

  const errorCode = response?.data?.httpErrorCode?.status;
  if (errorCode) {
    throw new Error('Failed to fetch sensor data');
  }

  return response.data.map((d: any) => ({
    cm: d.avg_cm ?? null,
    EE: d.avg_EE ?? null,
    Te: d.avg_Te ?? null,
    l: d.avg_l ?? null,
    m2: d.avg_m2 ?? null,
    log_count: d.log_count,
    createTime: new Date(d.bucket_start.value),
    id: d.id,
  }));
};

function filterSeriesByRange<T extends { rawTimestamp: Date }>(
  series: T[],
  range: InclusiveDateRange | null
): T[] {
  if (!range) {
    return series;
  }

  return series.filter((item) => isDateWithinInclusiveRange(item.rawTimestamp, range));
}

// Process raw logs into chart data
const processLevelData = (
  logs: Log[],
  deviceId: string,
  selectedRange: InclusiveDateRange | null
): ProcessedLevelData => {
  const allLevelData: ChartData[] = [];
  const allLiterData: ChartDataLiter[] = [];
  const allTempData: ChartDataTemp[] = [];

  logs.forEach((flowData) => {
    const level = flowData.cm ?? 0;
    const temp = flowData.Te ?? 0;
    const liter = (flowData.l ?? 0) + (flowData.m2 ?? 0) * 1000;

    const timestamp = flowData.createTime;
    const formattedTime = `${timestamp.toLocaleDateString()} ${timestamp.toLocaleTimeString()}`;

    allLevelData.push({
      timestamp: timestamp.toISOString(),
      level,
      formattedTime,
      rawTimestamp: timestamp,
      deviceId,
    });

    allLiterData.push({
      timestamp: timestamp.toISOString(),
      liter,
      formattedTime,
      rawTimestamp: timestamp,
      deviceId,
    });

    allTempData.push({
      timestamp: timestamp.toISOString(),
      temp,
      formattedTime,
      rawTimestamp: timestamp,
      deviceId,
    });
  });

  // Sort by timestamp
  allLevelData.sort((a, b) => a.rawTimestamp.getTime() - b.rawTimestamp.getTime());
  allLiterData.sort((a, b) => a.rawTimestamp.getTime() - b.rawTimestamp.getTime());
  allTempData.sort((a, b) => a.rawTimestamp.getTime() - b.rawTimestamp.getTime());

  const levelData = filterSeriesByRange(allLevelData, selectedRange);
  const literData = filterSeriesByRange(allLiterData, selectedRange);
  const tempData = filterSeriesByRange(allTempData, selectedRange);

  const maxLevel = levelData.reduce((currentMax, item) => Math.max(currentMax, item.level), 0);
  const maxLiter = literData.reduce((currentMax, item) => Math.max(currentMax, item.liter), 0);

  return {
    levelData,
    literData,
    tempData,
    weekSummaryLevelData: allLevelData,
    maxLevel: Math.max(maxLevel, 100), // Ensure minimum of 100
    maxLiter: Math.max(maxLiter, 100), // Ensure minimum of 100
    latestLog: levelData[levelData.length - 1],
  };
};

// Custom hook
export const useLevelSensorData = (deviceId: string, dateRange: DateRange | undefined) => {
  const ranges = React.useMemo(
    () => normalizeHistoryDateRanges(dateRange),
    [dateRange?.from?.getTime(), dateRange?.to?.getTime()]
  );

  const query = useQuery({
    queryKey: [
      'levelSensorData',
      deviceId,
      ranges.summaryFetchRange?.from?.toISOString(),
      ranges.summaryFetchRange?.to?.toISOString(),
    ],
    queryFn: async (): Promise<ProcessedLevelData> => {
      const logs = await getLogs(deviceId, ranges.summaryFetchRange ?? undefined);
      return processLevelData(logs, deviceId, ranges.selectedRange);
    },
    enabled: !!deviceId && !!ranges.summaryFetchRange?.from && !!ranges.summaryFetchRange?.to,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Handle success/error notifications using useEffect
  React.useEffect(() => {
    if (query.isSuccess && ranges.selectedRange?.from && ranges.selectedRange?.to) {
      toast.success(
        `Data blev hentet for id:${deviceId} fra ${format(
          ranges.selectedRange.from,
          'yyyy-MM-dd'
        )} til ${format(ranges.selectedRange.to, 'yyyy-MM-dd')}`
      );
    }
    if (query.isError) {
      console.error('Error fetching level sensor data:', query.error);
      toast.error('Der skete en fejl ved hentning af data');
    }
  }, [query.isSuccess, query.isError, query.error, deviceId, ranges.selectedRange]);

  return query;
};

// Hook for getting current level (latest reading)
export const useCurrentLevel = (deviceId: string, dateRange: DateRange | undefined) => {
  const { data, isLoading, error } = useLevelSensorData(deviceId, dateRange);

  return {
    currentLevel: data?.latestLog?.level || 0,
    currentLevelCm: data?.latestLog?.level || 0,
    isLoading,
    error,
    lastUpdated: data?.latestLog?.rawTimestamp,
  };
};
